import {
  type ButtonHTMLAttributes,
  forwardRef,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  useEffect,
  useRef,
} from "react";

export type CalmStatus =
  | "covered"
  | "partially_covered"
  | "at_risk"
  | "needs_confirmation"
  | "paid"
  | "plan_updated"
  | "data_stale"
  | "no_action_needed"
  | "safe_now"
  | "safe_with_tradeoff"
  | "wait_until"
  | "not_safe_current_plan"
  | "cannot_determine"
  | "updating"
  | "pending";

export const STATUS_LABELS: Record<CalmStatus, string> = {
  covered: "Covered",
  partially_covered: "Partially covered",
  at_risk: "Needs attention",
  needs_confirmation: "Needs confirmation",
  paid: "Paid",
  plan_updated: "Plan updated",
  data_stale: "Data stale",
  no_action_needed: "No action needed",
  safe_now: "Safe now",
  safe_with_tradeoff: "Safe with tradeoff",
  wait_until: "Wait until",
  not_safe_current_plan: "Not safe in current plan",
  cannot_determine: "Cannot determine",
  updating: "Updating",
  pending: "Pending",
};

export function cn(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(" ");
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "quiet" | "danger";
  isLoading?: boolean;
};

export function Button({
  children,
  className,
  variant = "primary",
  isLoading = false,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <button
      className={cn("mm-button", `mm-button--${variant}`, className)}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? <span className="mm-spinner" aria-hidden="true" /> : null}
      <span>{isLoading ? "Working…" : children}</span>
    </button>
  );
}

export function Card({
  children,
  className,
  as: Element = "section",
  ...props
}: HTMLAttributes<HTMLElement> & { as?: "article" | "section" | "div" }) {
  return (
    <Element className={cn("mm-card", className)} {...props}>
      {children}
    </Element>
  );
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="mm-page-header">
      <div>
        {eyebrow ? <p className="mm-eyebrow">{eyebrow}</p> : null}
        <h1>{title}</h1>
        {description ? <p className="mm-page-description">{description}</p> : null}
      </div>
      {actions ? <div className="mm-page-actions">{actions}</div> : null}
    </header>
  );
}

export function SectionHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="mm-section-header">
      <div>
        <h2>{title}</h2>
        {description ? <p>{description}</p> : null}
      </div>
      {action}
    </div>
  );
}

export function StatusBadge({ status }: { status: CalmStatus }) {
  return (
    <span className={cn("mm-status", `mm-status--${status}`)}>
      <span className="mm-status__dot" aria-hidden="true" />
      {STATUS_LABELS[status]}
    </span>
  );
}

export function Callout({
  title,
  children,
  tone = "info",
  role,
}: {
  title: string;
  children: ReactNode;
  tone?: "info" | "positive" | "warning" | "urgent";
  role?: "alert" | "status";
}) {
  return (
    <div className={cn("mm-callout", `mm-callout--${tone}`)} role={role}>
      <strong>{title}</strong>
      <div>{children}</div>
    </div>
  );
}

export function FormField({
  label,
  htmlFor,
  hint,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string | undefined;
  error?: string | undefined;
  children: ReactNode;
}) {
  const hintId = hint ? `${htmlFor}-hint` : undefined;
  const errorId = error ? `${htmlFor}-error` : undefined;
  return (
    <div className="mm-field">
      <label htmlFor={htmlFor}>{label}</label>
      {hint ? (
        <span className="mm-field__hint" id={hintId}>
          {hint}
        </span>
      ) : null}
      {children}
      {error ? (
        <span className="mm-field__error" id={errorId} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function TextInput({ className, ...props }, ref) {
    return <input ref={ref} className={cn("mm-input", className)} {...props} />;
  },
);

export const SelectInput = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function SelectInput({ className, children, ...props }, ref) {
    return (
      <select ref={ref} className={cn("mm-input", className)} {...props}>
        {children}
      </select>
    );
  },
);

export function MoneyAmount({
  minor,
  currency,
  className,
  sign = "auto",
}: {
  minor: string;
  currency: string;
  className?: string;
  sign?: "auto" | "always" | "never";
}) {
  let value = 0;
  try {
    value = Number(BigInt(minor)) / 100;
  } catch {
    return <span className={className}>Amount unavailable</span>;
  }
  const formatted = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    signDisplay: sign === "always" ? "always" : sign === "never" ? "never" : "auto",
  }).format(value);
  return <span className={className}>{formatted}</span>;
}

export function ProgressMeter({
  label,
  value,
  helper,
}: {
  label: string;
  value: number;
  helper?: string;
}) {
  const safeValue = Math.min(100, Math.max(0, value));
  return (
    <div className="mm-progress">
      <div className="mm-progress__label">
        <span>{label}</span>
        <span>{helper ?? `${String(Math.round(safeValue))}%`}</span>
      </div>
      <div
        className="mm-progress__track"
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(safeValue)}
      >
        <span style={{ width: `${String(safeValue)}%` }} />
      </div>
    </div>
  );
}

export function StatePanel({
  title,
  children,
  action,
  kind = "empty",
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
  kind?: "empty" | "loading" | "error" | "offline";
}) {
  return (
    <div
      className={cn("mm-state", `mm-state--${kind}`)}
      role={kind === "error" ? "alert" : "status"}
    >
      {kind === "loading" ? (
        <span className="mm-spinner mm-spinner--large" aria-hidden="true" />
      ) : null}
      <h2>{title}</h2>
      <div>{children}</div>
      {action ? <div className="mm-state__action">{action}</div> : null}
    </div>
  );
}

export function Modal({
  isOpen,
  title,
  description,
  onClose,
  children,
  footer,
}: {
  isOpen: boolean;
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!isOpen) return undefined;
    const previouslyFocused = document.activeElement;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    closeButtonRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;
  return (
    <div className="mm-modal-backdrop" role="presentation">
      <div
        className="mm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mm-modal-title"
        aria-describedby={description ? "mm-modal-description" : undefined}
      >
        <div className="mm-modal__header">
          <div>
            <h2 id="mm-modal-title">{title}</h2>
            {description ? <p id="mm-modal-description">{description}</p> : null}
          </div>
          <button
            className="mm-icon-button"
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            ref={closeButtonRef}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <div className="mm-modal__body">{children}</div>
        {footer ? <div className="mm-modal__footer">{footer}</div> : null}
      </div>
    </div>
  );
}

export function EvidenceList({
  items,
  title = "How IterNest Money knows",
}: {
  items: Array<{
    label: string;
    value?: string | undefined;
    source?: string | undefined;
    confidence?: string | undefined;
    observedAt?: string | undefined;
  }>;
  title?: string;
}) {
  return (
    <details className="mm-evidence">
      <summary>{title}</summary>
      {items.length === 0 ? (
        <p>No evidence details were returned.</p>
      ) : (
        <dl>
          {items.map((item, index) => (
            <div key={item.label + String(index)}>
              <dt>{item.label}</dt>
              <dd>
                {item.value ?? "Recorded input"}
                {item.confidence ? <span> · {item.confidence}</span> : null}
                {item.source ? <span> · {item.source}</span> : null}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </details>
  );
}

export function VisuallyHidden({ children }: { children: ReactNode }) {
  return <span className="mm-visually-hidden">{children}</span>;
}
